import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import cx from 'classnames'
import Markdown from 'react-markdown'

import { BlockIndicator } from '../../components/blockIndicator/BlockIndicator'
import { DraftIndicator } from '../../components/draftIndicator/DraftIndicator'
import { LayerIndicator } from '../../components/layerIndicator/LayerIndicator'
import type { EnumLiteral } from '../../uiModels/enumLiterals/types'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'

type Props = {
  data: EnumLiteral
  column: number
  row?: number
}

export const EnumLiteralRowCell = ({ data, column }: Props) => {
  const { applicationEnumDefinitions } = useApplicationViewOperationsContext()
  const literalName =
    data.type === 'modeled'
      ? data.literalName
      : data.internalOperation?.entity?.name
  assertNotEmpty(literalName)

  const translate = useTranslate()
  const enumDefinition = applicationEnumDefinitions?.get(
    data.type === 'modeled'
      ? data.enumDefinition.typeName
      : (data.internalOperation.entity?.enumName ?? ''),
  )
  assertNotEmpty(enumDefinition)

  const literalLayer = data.type === 'configured' ? data.layer : null
  const literalBlock = data.type === 'configured' ? data.block : null

  switch (column) {
    case 0:
      return (
        <div className="flex size-full items-center justify-start gap-x-2">
          <h4
            className={cx('m-0 pl-2 font-medium', {
              'line-through':
                data.type === 'configured' &&
                !!data.internalOperation.entity?.deprecated,
            })}
          >
            {literalName}
          </h4>
          {data.type === 'configured' && data.isDraft ? (
            <DraftIndicator size="small" />
          ) : null}
          {!!literalBlock && (
            <BlockIndicator
              block={literalBlock}
              shouldShowIcon={false}
              isSecondaryBackground={false}
            />
          )}
          {!!literalLayer && (
            <LayerIndicator
              layer={literalLayer}
              isSecondaryBackground={false}
            />
          )}
        </div>
      )
    case 1:
      return (
        <div>
          {data.type === 'modeled'
            ? translate('enumViewer.enumType.modeled')
            : translate('enumViewer.enumType.configured')}
        </div>
      )
    case 2:
      return (
        <div className="flex size-full items-center p-2">
          {data.type === 'modeled' ? (
            '-'
          ) : (
            <Markdown>
              {data.internalOperation.entity?.description ?? '\\-'}
            </Markdown>
          )}
        </div>
      )
    default:
      throw new Error(
        `Invalid table column. No data available for column ${column}`,
      )
  }
}
